/**
 * Admin → Settings → Jev → Thresholds — E07-F12-T03 (real API via jevApi → real/jev.ts).
 *
 * Screen `jev-thresholds`: per question set — the current thresholds, the
 * version history (date, source, reason), rollback to any version, freeze of the
 * automatic recalculation, safeguard warnings (§5.6), and the PAID
 * recalibration behind a confirmation that shows the row count and the price.
 *
 * Automatic recalculation itself is free arithmetic on stored probabilities and
 * labels (no Jev calls); only the recalibration button spends money.
 */
import { useState } from 'react';
import { Dialog, DialogFooter, useConfirm, useToast } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import {
  useJevQuestionSets,
  useJevRecalibrationQuote,
  useJevThresholds,
  useRollbackJevThresholds,
  useSetJevFreeze,
  useStartJevRecalibration,
  type JevSetKey,
  type JevSetThresholds,
} from './jevApi.js';
import {
  JUNK_MIN_POSITIVES,
  VERSION_SOURCE_LABEL,
  formatDateTime,
  formatInt,
  formatProb,
  formatUsd,
  type JevQuestionSet,
  type JevVersionSource,
} from './jevModel.js';
import {
  BTN_OUTLINE,
  BTN_PAID,
  Card,
  Loading,
  Notice,
  Pill,
  SectionHeading,
  type PillTone,
} from './jevUi.js';

const SOURCE_TONE: Record<JevVersionSource, PillTone> = {
  auto: 'neutral',
  rollback: 'info',
  reset: 'warning',
  recalibration: 'success',
  initial: 'success',
};

function RecalibrationDialog({
  setKey,
  onClose,
}: {
  readonly setKey: JevSetKey;
  readonly onClose: () => void;
}): React.JSX.Element {
  const quote = useJevRecalibrationQuote(setKey);
  const start = useStartJevRecalibration();
  const toast = useToast();
  const q = quote.data;

  function confirm(): void {
    if (!q) return;
    // The server re-checks this count against its own preview (409 if rows changed meanwhile).
    start.mutate(
      { setKey, confirmedRows: q.rows },
      {
        onSuccess: () => {
          toast.success(`Recalibration of ${setKey} started`);
          onClose();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not start the recalibration')),
      },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Recalibrate on labelled rows?"
      testId="jev-recalibrate-dialog"
      maxWidth="md"
      footer={
        <DialogFooter
          onCancel={onClose}
          onConfirm={confirm}
          confirmDisabled={!q || q.rows === 0 || start.isPending}
          confirmLabel={q ? `Start — ${formatUsd(q.totalUsd)}` : 'Start'}
          variant="accent"
          testId="jev-recalibrate-dialog"
        />
      }
    >
      {quote.isLoading ? <Loading testId="jev-recalibrate-quote-loading" /> : null}
      {quote.isError ? (
        <Notice tone="danger">
          {errorMessage(quote.error, 'Could not price the recalibration')}
        </Notice>
      ) : null}
      {q ? (
        <div className="grid gap-4 text-sm text-text">
          <p>
            Jev will be called once for every labelled row of{' '}
            <code className="rounded bg-gray-100 px-1 text-xs">{setKey}</code>, and new thresholds
            will be fitted to those answers and the human labels.
          </p>
          <div className="grid grid-cols-3 gap-3 rounded-lg bg-[var(--color-surface-muted)] p-3 text-center">
            <div>
              <div
                className="text-xl font-semibold text-text-strong"
                data-testid="jev-recalibrate-rows"
              >
                {formatInt(q.rows)}
              </div>
              <div className="text-[11px] text-text-muted">rows = paid calls</div>
            </div>
            <div>
              <div className="text-xl font-semibold text-text-strong">
                {formatUsd(q.costPerRowUsd)}
              </div>
              <div className="text-[11px] text-text-muted">
                {q.isEstimate ? 'per call (estimate)' : 'avg. per call, from the log'}
              </div>
            </div>
            <div>
              <div
                className="text-xl font-semibold text-primary"
                data-testid="jev-recalibrate-total"
              >
                {formatUsd(q.totalUsd)}
              </div>
              <div className="text-[11px] text-text-muted">
                {q.isEstimate ? 'total (estimate)' : 'total'}
              </div>
            </div>
          </div>
          {q.rows === 0 ? (
            <Notice tone="info" testId="jev-recalibrate-empty">
              There are no labelled rows Jev can be re-run on for this set yet — nothing to
              recalibrate.
            </Notice>
          ) : null}
          {q.isEstimate ? (
            <p className="text-xs text-text-muted">
              This set has no call history yet, so the price per call is a conservative constant.
            </p>
          ) : null}
          <p className="text-xs text-text-muted">
            Runs in the background. The new thresholds become a new version when it finishes and
            must pass the safeguard; while it runs, the set&apos;s questions cannot be saved.
          </p>
        </div>
      ) : null}
    </Dialog>
  );
}

function SetThresholdsCard({
  t,
  set,
  jevReady,
  onRecalibrate,
}: {
  readonly t: JevSetThresholds;
  readonly set: JevQuestionSet | undefined;
  readonly jevReady: boolean;
  readonly onRecalibrate: () => void;
}): React.JSX.Element {
  const freeze = useSetJevFreeze();
  const rollback = useRollbackJevThresholds();
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [historyOpen, setHistoryOpen] = useState(t.setKey === 'research_triage');
  const current = t.versions.find((v) => v.version === t.currentVersion) ?? t.versions[0];
  // The junk bin exists only in the triage set (§5.6) — the hint is meaningless elsewhere.
  const junkLocked = t.setKey === 'research_triage' && t.positives < JUNK_MIN_POSITIVES;

  async function onRollback(version: number): Promise<void> {
    const ok = await confirm({
      title: `Roll back to v${version}?`,
      message: `The thresholds of ${t.setKey} will be set to the values of v${version}. This becomes a new version; nothing is deleted from the history. No Jev calls are made.`,
      confirmLabel: `Roll back to v${version}`,
      testId: 'jev-rollback-confirm',
    });
    if (!ok) return;
    rollback.mutate(
      { setKey: t.setKey, version },
      {
        onSuccess: () => toast.success(`${t.setKey}: rolled back to v${version}`),
        onError: (err) => toast.error(errorMessage(err, 'Rollback failed')),
      },
    );
  }

  function onFreeze(): void {
    const frozen = !t.frozen;
    freeze.mutate(
      { setKey: t.setKey, frozen },
      {
        onSuccess: () =>
          toast.success(
            frozen
              ? `${t.setKey}: automatic recalculation frozen`
              : `${t.setKey}: automatic recalculation resumed`,
          ),
        onError: (err) => toast.error(errorMessage(err, 'Could not change the freeze')),
      },
    );
  }

  return (
    <Card testId={`jev-thresholds-${t.setKey}`}>
      {confirmDialog}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-text-strong">{set?.title ?? t.setKey}</h4>
            <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700">
              {t.setKey}
            </code>
            <Pill tone="primary">v{t.currentVersion}</Pill>
            {t.frozen ? (
              <Pill tone="info" testId={`jev-thresholds-${t.setKey}-frozen`}>
                <i className="fa-solid fa-snowflake" aria-hidden /> Auto-recalculation frozen
              </Pill>
            ) : (
              <Pill tone="success">
                <i className="fa-solid fa-rotate" aria-hidden /> Auto-recalculation on
              </Pill>
            )}
            {set && !set.calibrated ? (
              <Pill tone="warning">Not calibrated — safe defaults</Pill>
            ) : null}
          </div>
          <p className="mt-1 text-[11px] text-text-muted">
            {formatInt(t.labels)} human labels · {formatInt(t.positives)} positives
            {junkLocked ? ` · junk bin stays off until ${JUNK_MIN_POSITIVES} positives` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid={`jev-thresholds-${t.setKey}-freeze`}
            onClick={onFreeze}
            disabled={freeze.isPending}
            className={BTN_OUTLINE}
          >
            <i className={`fa-solid ${t.frozen ? 'fa-play' : 'fa-snowflake'} mr-1.5`} aria-hidden />
            {t.frozen ? 'Resume auto-recalculation' : 'Freeze auto-recalculation'}
          </button>
          <button
            type="button"
            data-testid={`jev-thresholds-${t.setKey}-recalibrate`}
            onClick={onRecalibrate}
            disabled={!jevReady || t.recalibration !== null}
            title={
              !jevReady
                ? 'Jev is not configured'
                : t.recalibration
                  ? 'A recalibration is already running'
                  : undefined
            }
            className={BTN_PAID}
          >
            <i className="fa-solid fa-coins" aria-hidden />
            Recalibrate…
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-2">
        {t.warnings.map((w, i) => (
          <Notice key={i} tone="warning" testId={`jev-thresholds-${t.setKey}-warning`}>
            <strong>Safeguard · {formatDateTime(w.at)}.</strong> {w.message}
          </Notice>
        ))}
        {t.frozen ? (
          <Notice tone="info">
            New labels are still recorded, but thresholds do not move until you resume. Rollback and
            recalibration still work.
          </Notice>
        ) : null}
        {t.recalibration ? (
          <div
            data-testid={`jev-thresholds-${t.setKey}-recalibrating`}
            className="rounded-lg border border-border bg-[var(--color-surface-muted)] p-3"
          >
            <div className="mb-2 flex items-center justify-between text-xs">
              <span className="font-medium text-text-strong">
                <i className="fa-solid fa-spinner fa-spin mr-1.5" aria-hidden />
                Recalibrating — {t.recalibration.done} of {t.recalibration.rows} rows
              </span>
              <span className="text-text-muted">
                started {formatDateTime(t.recalibration.startedAt)}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full bg-[var(--color-progress)] transition-[width] duration-700"
                style={{
                  width: `${Math.round((t.recalibration.done / t.recalibration.rows) * 100)}%`,
                }}
              />
            </div>
          </div>
        ) : null}
      </div>

      {current ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {current.values.map((v) => (
            <div key={v.name} className="rounded-lg border border-border px-3 py-2">
              <div className="text-[11px] text-text-muted">{v.label}</div>
              <div className="mt-0.5 flex items-baseline gap-2">
                <span
                  data-testid={`jev-thresholds-${t.setKey}-${v.name}`}
                  className={`font-mono text-xl font-semibold ${v.value === null ? 'text-gray-400' : 'text-text-strong'}`}
                >
                  {formatProb(v.value)}
                </span>
                <code className="text-[10px] text-gray-400">{`{${v.name}}`}</code>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-4 border-t border-border pt-3">
        <button
          type="button"
          onClick={() => setHistoryOpen((o) => !o)}
          aria-expanded={historyOpen}
          data-testid={`jev-thresholds-${t.setKey}-history-toggle`}
          className="text-xs font-medium text-text-strong hover:text-primary"
        >
          <i
            className={`fa-solid fa-chevron-${historyOpen ? 'up' : 'down'} mr-1.5 text-[10px]`}
            aria-hidden
          />
          Version history ({t.versions.length})
        </button>
        {historyOpen ? (
          <div className="mt-3 overflow-x-auto">
            <table
              className="w-full min-w-[40rem] text-left text-xs"
              data-testid={`jev-thresholds-${t.setKey}-history`}
            >
              <thead className="text-[11px] text-text-muted">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3 font-medium">Version</th>
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Reason</th>
                  <th className="py-2 pr-3 font-medium">Values</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {t.versions.map((v) => {
                  const isCurrent = v.version === t.currentVersion;
                  return (
                    <tr
                      key={v.version}
                      className={`border-b border-border align-top ${isCurrent ? 'bg-amber-50/60' : ''}`}
                    >
                      <td className="py-2 pr-3 font-mono font-semibold text-text-strong">
                        v{v.version}
                        {isCurrent ? (
                          <span className="ml-1 font-sans text-[10px] font-normal text-amber-700">
                            current
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 text-text-muted">
                        {formatDateTime(v.createdAt)}
                      </td>
                      <td className="py-2 pr-3">
                        <Pill tone={SOURCE_TONE[v.source]}>{VERSION_SOURCE_LABEL[v.source]}</Pill>
                        <div className="mt-1 text-text">{v.reason}</div>
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 font-mono text-text-muted">
                        {v.values.map((x) => `${x.name} ${formatProb(x.value)}`).join(' · ')}
                      </td>
                      <td className="py-2 text-right">
                        {!isCurrent ? (
                          <button
                            type="button"
                            data-testid={`jev-thresholds-${t.setKey}-rollback-v${v.version}`}
                            onClick={() => void onRollback(v.version)}
                            disabled={rollback.isPending}
                            className="whitespace-nowrap text-xs font-medium text-link hover:underline disabled:opacity-50"
                          >
                            <i className="fa-solid fa-clock-rotate-left mr-1" aria-hidden />
                            Roll back
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export function JevThresholds({ jevReady }: { readonly jevReady: boolean }): React.JSX.Element {
  const thresholdsQuery = useJevThresholds();
  const setsQuery = useJevQuestionSets();
  const [recalibrating, setRecalibrating] = useState<JevSetKey | null>(null);

  if (thresholdsQuery.isLoading) return <Loading testId="jev-thresholds-loading" />;
  if (thresholdsQuery.isError || !thresholdsQuery.data) {
    return (
      <Notice tone="danger" testId="jev-thresholds-error">
        {errorMessage(thresholdsQuery.error, 'Failed to load thresholds')}
      </Notice>
    );
  }

  return (
    <div data-testid="jev-thresholds" className="grid gap-4">
      <SectionHeading
        title="Thresholds"
        hint="After every new human label the code recalculates the band thresholds from stored probabilities — free, no Jev calls. New thresholds apply only if they pass the safeguard: no positive lands in Probable junk, and needs-human lets no A/B through that a human later lowered to C."
      />
      {thresholdsQuery.data.map((t) => (
        <SetThresholdsCard
          key={t.setKey}
          t={t}
          set={setsQuery.data?.find((s) => s.key === t.setKey)}
          jevReady={jevReady}
          onRecalibrate={() => setRecalibrating(t.setKey)}
        />
      ))}
      {recalibrating ? (
        <RecalibrationDialog setKey={recalibrating} onClose={() => setRecalibrating(null)} />
      ) : null}
    </div>
  );
}
